/**
 * ClearanceService — Low Clearance Detection
 * Asks the clearances Lambda (OpenStreetMap maxheight data imported into
 * DynamoDB, see scripts/clearances/) which height-limited structures the route
 * passes through, and compares them against the user's rig height.
 *
 * A failed lookup returns an empty list flagged `unavailable`, so the app can
 * say "couldn't check" instead of implying the route is clear.
 */
import * as units from './units.js';

const API_URL = import.meta.env?.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';
const M_TO_FT = 1 / 0.3048;
// Stays under the Lambda's 20,000-point limit per request.
const CHUNK_POINTS = 15000;

// Road the route is on after a step, read from the routing service's
// instruction text ("Take ramp onto A4 (Western Distributor) toward ...",
// "Continue on Railway Pde (A41). Go for ..."), which carries road names when
// the structured road fields are empty. Unrecognised text yields no road, and
// the server then matches on shape alone.
export function roadFromInstruction(instruction) {
    const m = /\b(?:onto|on)\s+(.+?)(?:\s+toward\b|\.\s|\.$|$)/i.exec(instruction || '');
    if (!m) return [null, null];
    const road = m[1].trim();
    const paren = /^(.+?)\s*\((.+)\)$/.exec(road);
    // Route numbers like A4, M8, I-70, US-40, optionally with a direction ("I-70 W").
    const number = (t) => {
        const n = /^([A-Z]{0,3}-?\d+[A-Z]?)(?:\s+[NSEW])?$/i.exec(t.trim());
        return n ? n[1] : null;
    };
    if (paren) {
        const [a, b] = [paren[1].trim(), paren[2].trim()];
        return number(a) ? [b, number(a)] : number(b) ? [a, number(b)] : [road, null];
    }
    return number(road) ? [null, number(road)] : [road, null];
}

export class ClearanceService {
    constructor() {
        this.cache = new Map();
    }

    /**
     * Check for low clearances along a route
     * @param {Array} coordinates - Route coordinates [{lat, lng}, ...]
     * @param {number} rigHeightFt - Rig height in feet
     * @param {object} options - { bufferFt: 1.0, steps: route steps with road names (optional) }
     * @returns {Array} Clearance warnings; `.unavailable` is true if the check couldn't run
     */
    async checkClearances(coordinates, rigHeightFt, options = {}) {
        const bufferFt = options.bufferFt || 1.0;
        const requiredClearanceFt = rigHeightFt + bufferFt;

        if (!coordinates || coordinates.length < 2 || !rigHeightFt) {
            return [];
        }

        let clearances;
        try {
            clearances = await this.fetchClearances(coordinates, options.steps);
        } catch (error) {
            console.warn('Clearance check unavailable:', error.message);
            const unavailable = [];
            unavailable.unavailable = true;
            return unavailable;
        }

        const warnings = clearances
            .map(c => {
                const marginFt = c.clearanceFt - rigHeightFt;
                let severity;
                if (c.clearanceFt < rigHeightFt) {
                    severity = 'danger';
                } else if (c.clearanceFt < rigHeightFt + 0.5) {
                    severity = 'warning';
                } else if (c.clearanceFt < requiredClearanceFt) {
                    severity = 'notice';
                } else {
                    return null;
                }

                return {
                    ...c,
                    rigHeightFt,
                    marginFt: Math.round(marginFt * 10) / 10,
                    severity,
                    message: this.getWarningMessage(severity, c.clearanceFt, rigHeightFt)
                };
            })
            .filter(Boolean);

        console.log(`🚧 Clearance check: ${clearances.length} on route, ${warnings.length} warnings`);
        return warnings;
    }

    /**
     * All height-limited structures the route passes through, in feet. Throws
     * if any part of the route couldn't be checked.
     */
    async fetchClearances(coordinates, steps = []) {
        const points = coordinates.map(c => [c.lng, c.lat]);
        // Road names along the route let the server tell a clearance on the
        // route's road from one on a side street or a road passing beneath.
        const roads = (steps || [])
            .filter(s => Number.isInteger(s.geometryOffset))
            .map(s => {
                if (s.currentRoad || s.currentRouteNumber) {
                    return [s.geometryOffset, s.currentRoad || null, s.currentRouteNumber || null];
                }
                return [s.geometryOffset, ...roadFromInstruction(s.instruction)];
            });
        const first = points[0], last = points[points.length - 1];
        const cacheKey = `${points.length}:${first}:${last}`;
        if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);

        const byId = new Map();
        for (let start = 0; start < points.length - 1; start += CHUNK_POINTS - 1) {
            const chunk = points.slice(start, start + CHUNK_POINTS);
            const chunkRoads = roads
                .filter(([offset], i) => offset >= start || !(roads[i + 1] && roads[i + 1][0] <= start))
                .filter(([offset]) => offset < start + chunk.length)
                .map(([offset, name, number]) => [Math.max(0, offset - start), name, number]);
            const response = await fetch(`${API_URL}/api/clearances`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ points: chunk, roads: chunkRoads })
            });
            if (!response.ok) throw new Error(`clearances API returned ${response.status}`);
            const data = await response.json();
            for (const c of data.clearances || []) byId.set(c.featureId, c);
        }

        const clearances = [...byId.values()].map(c => {
            const clearanceFt = c.clearanceM * M_TO_FT;
            const [type, osmId] = c.featureId.replace(/^osm:/, '').split('/');
            return {
                lat: c.lat,
                lng: c.lng,
                clearanceFt,
                clearanceFormatted: this.formatHeight(clearanceFt),
                osmId: Number(osmId),
                type,
                name: c.name || 'Low clearance',
                // Road to name in the spoken warning ("on US-6"), or null.
                road: c.road || null,
                source: 'osm'
            };
        });
        this.cache.set(cacheKey, clearances);
        return clearances;
    }

    /**
     * Format a height for display. Metric rounds clearances down and the rig's
     * height up, so rounding can never make a tight fit look roomier.
     */
    formatHeight(ft, mode = 'down') {
        if (units.isMetric()) return `${units.height(ft, mode).toFixed(1)} m`;
        const feet = Math.floor(ft);
        const inches = Math.round((ft - feet) * 12);
        if (inches === 12) return `${feet + 1}'0"`;
        if (inches === 0) return `${feet}'0"`;
        return `${feet}'${inches}"`;
    }

    /**
     * Get human-readable warning message
     */
    getWarningMessage(severity, clearanceFt, rigHeightFt) {
        const formatted = this.formatHeight(clearanceFt, 'down');
        const rigFormatted = this.formatHeight(rigHeightFt, 'up');
        const [tight, veryTight] = units.isMetric() ? ['30 cm', '15 cm'] : ['12"', '6"'];

        switch (severity) {
            case 'danger':
                return `🚨 WILL NOT FIT — Clearance ${formatted}, your rig is ${rigFormatted}`;
            case 'warning':
                return `⚠️ VERY TIGHT — Clearance ${formatted}, your rig is ${rigFormatted} (less than ${veryTight} margin)`;
            case 'notice':
                return `📋 TIGHT — Clearance ${formatted}, your rig is ${rigFormatted} (less than ${tight} margin)`;
            default:
                return '';
        }
    }
}
